// TabContent1.js
import React, { useState, useEffect, useRef } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, InputBase, Typography } from '@mui/material';
import { tokens } from '../../../../../theme';
import { useDispatch, useSelector } from 'react-redux';

import { InputDropdown } from "../../../../theme/dropdowns/InputDropdown";
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import * as yup from "yup";

import { yupRequired } from '../../../../../utils/validation';
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import NumberInput from '../../../../theme/inputs/NumberInput';
import Shbutton from '../../../../theme/buttons/Shbutton';
import { Delete } from '@mui/icons-material';
import { useGetAvailableWarehousesMutation, useGetAvailableWarehousesStatusMutation } from '../../../../../newapi/webshop/bolreplenishmentSlice';
import ShLabel from '../../../../theme/label/ShLabel';
import ShDateRangePicker from '../../../../theme/ShDateRangePicker';
import { useGetSettingsForRelationQuery } from '../../../../../newapi/global/generalApiSlice';


/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    // Hydrate from the shared progression data so selections survive the unmount
    // caused by navigating back to a previous tab (e.g. to fix a rejected reference).
    const [selectedProducts, setSelectedProducts] = useState(props.data?.products ?? []);
    const [getAvailableWarehouses, {isLoading: isLoadingWarehouses}] = useGetAvailableWarehousesMutation();
    const [getAvailableWarehousesStatus, {isLoading: isLoadingWarehousesStatus}] = useGetAvailableWarehousesStatusMutation();
    const [destinationValid, setDestinationValid] = useState(false);
    const [destinationsLoading, setDestinationsLoading] = useState(false);

    // The warehouse saves this option as a relation setting, so it is read from the relation settings.
    const { data: relationSettings } = useGetSettingsForRelationQuery();
    const allowLvbPickDate = relationSettings?.find(s => s.key === "relation_allow_lvb_pick_date")?.value === "true";

    const handleDeliveryDateChange = (date) => {
        props.handleDataUpdate({ pickDate: date[0].formattedStartDate });
    };

    const { data: products } = useGetProductsSelectQuery({webshopcredentialsId: props.data?.webshopcredential, addStock: true},
        {
            skip: !props.data?.webshopcredential
        }
    );
    
    const addProduct = (val) => {
        setSelectedProducts(prevProducts => {
            const exists = prevProducts.some(
                p => p.id === val.id && p.sku === val.sku
            );
            if (!exists) {
                return [...prevProducts, { ...val, quantity: 1 }];
            }
            return prevProducts;
        });
    };

    const updateQuantity = (id, e) => {
        setSelectedProducts(prevProducts => {
            return prevProducts.map(product => product.id === id ? {...product, quantity: e} : product);
        });
    };

    const handleDeleleteLineRow = (id) => {
        setSelectedProducts(prevProducts => prevProducts.filter(product => product.id !== id));
    };

    // Effect for updating products in parent component
    useEffect(() => {
        props.handleDataUpdate({ products: selectedProducts }); 
    }, [selectedProducts]);

    const controllerRef = useRef(null);
    const pollTimeoutRef = useRef(null);

    useEffect(() => {
        if (selectedProducts.length === 0) {
            props.setIsDisabled(true);
            return;
        }

        // Kill any old request or poll
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
        }

        const controller = new AbortController();
        controllerRef.current = controller;

        getAvailableWarehouses({
            webshopcredential: props.data?.webshopcredential,
            products: selectedProducts,
            signal: controller.signal,
        })
        .unwrap()
        .then((res) => {
            if (res?.processStatusId) {
                startPolling(res.processStatusId);
            }
        })
        .catch(err => {
            if (err.name === "AbortError"){
                 return;
            }
            // console.error(err);
        });
    }, [selectedProducts.length])

    const startPolling = (statusId) => {
        const poll = () => {
            setDestinationsLoading(true);
            
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            const controller = new AbortController();
            controllerRef.current = controller;

            getAvailableWarehousesStatus({
                processStatusId: statusId,
                webshopcredential: props.data?.webshopcredential,
                
            }).unwrap({signal: controller.signal})
                .then((res) => {
                    if (res.success === true) {
                        if (pollTimeoutRef.current) {
                            clearTimeout(pollTimeoutRef.current);
                        }
                        controllerRef.current = null;
                        if(res.data?.productDestinations){
                            setDestinationsLoading(false);
                            
                            const allEans = selectedProducts.map(product => product.sku);
                            const destination = res.data?.productDestinations?.find(dest => dest.eans.every(eanObj => allEans.includes(eanObj.ean)));
                            if (destination) {
                                setDestinationValid(true);
                                props.handleDataUpdate({ destination: destination.destinationWarehouse});
                                props.setIsDisabled(false);
                            } else {
                                props.setIsDisabled(true);
                                setDestinationValid(false);
                                props.handleDataUpdate({ destination: null});
                            }
                        } else {
                            props.setIsDisabled(true);
                            setDestinationsLoading(false);
                            setDestinationValid(false);
                            props.handleDataUpdate({ destination: null});
                        }
                    } else if (res.success === false && res.retry === true) {
                        pollTimeoutRef.current = setTimeout(poll, 5000);
                        props.setIsDisabled(true);
                    } else {
                        props.setIsDisabled(true);
                        setDestinationsLoading(false);
                        setDestinationValid(false);
                        props.handleDataUpdate({ destination: null});

                        if (pollTimeoutRef.current) {
                            clearTimeout(pollTimeoutRef.current);
                        }
                        controllerRef.current = null;
                    }
                })
            .catch((err) => {
                props.setIsDisabled(true);
                setDestinationsLoading(false);
                props.handleDataUpdate({ destination: null});

                if (pollTimeoutRef.current) {
                    clearTimeout(pollTimeoutRef.current);
                    pollTimeoutRef.current = null;
                }

                if (err.name === "AbortError") {
                    controllerRef.current = null;
                    return;
                }
            });
        };

        poll();
    };

  // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            <Box 
                sx={{
                    background: theme.palette.mode === "dark" ? colors.blue[100] : colors.blue[100],
                    color:theme.palette.mode === "dark" ? colors.blue[400] : colors.blue[400],
                    padding:2,
                    width:1,
                    maxWidth:500,
                    borderRadius:5,
                    mb:4,
                }}
            >
                <Typography fontWeight={"bold"} fontSize={17}>
                    {t("bol_replenishment_product_details_title")}
                </Typography>
                <Box sx={{paddingTop:0.5}}>
                    <Typography fontSize={14}>
                        {t("bol_replenishment_product_details_description")}
                    </Typography>
                </Box>
                        
                <Box 
                    sx={{
                        mt:2,
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:1
                    }}
                >


                    {destinationsLoading ? 
                        <ShLabel 
                            variant={theme.palette.mode === "dark" ? "greyLight" : "grey"} 
                            borderless 
                            palette={"normal"} 
                            size={32}
                        > 
                            {t("loading_available_warehouses")} ...
                        </ShLabel> 
                        : 
                        <ShLabel 
                            variant={destinationValid ? "green" : "red"}
                            borderless 
                            palette={"normal"} 
                            
                            size={32}
                        >
                            {selectedProducts.length > 0 ? (destinationValid ? t("destination_valid") : t("destination_invalid")) : t("no_products_selected")}
                        </ShLabel>
                    }
                </Box>
            </Box>

            {allowLvbPickDate && (
                <Box sx={{ width: 1, maxWidth: 500, mb: 4 }}>
                    <Box
                        sx={{
                            width: 1,
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 7,
                        }}
                    >
                        <Box
                            sx={{
                                width: 140,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                pt: 1,
                            }}
                        >
                            <Text>{t("desired_pick_date")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                borderRadius: 4,
                                display: "flex",
                                height: 32,
                            }}
                        >
                            <ShDateRangePicker
                                singleSelect={true}
                                startDate={data?.pickDate}
                                select={handleDeliveryDateChange}
                                size={32}
                            />
                        </Box>
                    </Box>
                    {/* The relation picks the picking date, shipping follows on the earliest possible date after it */}
                    <Box sx={{ mt: 1 }}>
                        <Text light>{t("desired_pick_date_explanation")}</Text>
                    </Box>
                </Box>
            )}

            <Box
                sx={{
                    width:1,
                    display:"flex",
                    // alignItems:"center",
                    justifyContent:"space-between",
                    flexDirection:'column',
                    gap:1,
                    pb:selectedProducts.length > 0 ? 2 : 0,
                    // pb:5,
                }}
            >

                <Box
                    sx={{
                        width:200,
                    }}
                >
                    <Text>{t("choose_product")}</Text>
                </Box>
                
                <InputDropdown
                    key={selectedProducts.length}
                    resetOnSelection
                    searchOptions={{
                        enabled: true,
                        placeholder: t("search_for_product"),
                        searchables: ["barcodes", "sku", "description"]
                    }}
                    displayName={"combined"} 
                    onChange={(selected) => addProduct(products[selected])}
                    noSelection={t("select_product_to_add")} //Custom text when nothing is selected
                    options={products ?? null}
                    selected={-1}
                    closeOnSelection
                />
                
            </Box>
            <Box
                sx={{
                    display:'flex',
                    flexDirection:'column',
                    gap:2,
                }}
            >
                {selectedProducts.map((product,idx) => (
                    <Box 
                        key={"selectedproduct"+idx}
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            flexDirection:"column",
                            gap:1,
                            // maxWidth:500,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                            p:2,
                            borderRadius:4,
                            position:'relative',
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                            }}
                        >
                            <Box
                                sx={{
                                    flex:1,
                                    borderRadius:5,
                                }}
                            >
                                <Text semibold>{product.combined}</Text>
                            </Box>

                            <Box
                                sx={{
                                    position:"absolute",
                                    top:0,
                                    right:0,
                                }}
                            >
                                <IconButton type="button" color="red" onClick={() => handleDeleleteLineRow(product.id)} sx={{p:1}}>
                                    <Delete/>
                                </IconButton>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width:1,
                                mt:2,
                                // width:200,
                            }}
                        >
                            <NumberInput
                                value={product.quantity ?? 1}
                                change={(e) => updateQuantity(product.id, e)}
                                onlyPositive
                                />
                        </Box>
                    </Box>
                ))}
            </Box>

        </>
    );
};

const ProductDetails = (props) => {
    const { t } = useTranslation();

    // const schema = yup.object({
    //     destination: yupRequired('destination',t),
    // });
    const [isDisabled, setIsDisabled] = useState(false);

    return (
        <ProgressionTab 
            tabId="tabProductDetails" 
            subtitle={t('product_details')} 
            step={2}
            maxSteps={2}
            nextDisabled={isDisabled}
            // removeButtons={true}
            // nextDisabled={true}
        //    schema={schema}
        >
            <Content 
                setIsDisabled={setIsDisabled}
            />
        </ProgressionTab>
    )
}

export default ProductDetails;